import React, { Fragment } from 'react';
import { isEmpty } from 'lodash';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import { getColor, seriesIsEmpty } from '../helpers';
import {
  Section,
  Header,
  Controls,
  LabeledControl,
  Checkbox,
  Divider,
  ColorPicker,
  Reset,
  SubHeader,
  Selection,
} from '../../reporting/sidebar/components';

function getColorKey(key) {
  return ['colors', String(key)];
}

// Special case for pie charts where we really only need to sort by number (pie size)
// Keeping default Option as a backup just incase.
function getSortOptionsForType(type) {
  const defaultOption = ['Default', 'none'];

  switch (type) {
    case 'number':
      return [
        defaultOption,
        ['Pie size 1 to 9', 'asc'],
        ['Pie size 9 to 1', 'desc'],
      ];
    default:
      return [defaultOption];
  }
}

function SeriesConfig({ showSort = false, sortType }) {
  const { get, meta } = useReportConfiguration();
  const chartType = get('chartType');
  const getSeriesColor = series => getColor(get(), meta, series);
  const getConfiguredColor = key => get(getColorKey(key));
  const emptySeries = seriesIsEmpty({ meta });
  const noDataColor = isEmpty(meta.seriesColors);

  return (
    <Section>
      <Header>Series</Header>
      <Controls>
        <LabeledControl title='Sync with data color'>
          <Checkbox
            configurationKey='useDataColor'
            disabled={emptySeries || noDataColor}
          />
        </LabeledControl>
        <Divider type='dark' />
        {emptySeries ? (
          <EmptySeriesColorPicker
            chartType={chartType}
            getSeriesColor={getSeriesColor}
            getConfiguredColor={getConfiguredColor}
          />
        ) : (
          <SeriesColorPickers
            meta={meta}
            getSeriesColor={getSeriesColor}
            getConfiguredColor={getConfiguredColor}
          />
        )}
      </Controls>
      {showSort && (
        <>
          <SubHeader>Sort Options</SubHeader>
          <Controls>
            <Selection
              options={getSortOptionsForType(sortType)}
              configurationKey='horizontalAxisSort'
              singleColumn
            />
          </Controls>
        </>
      )}
    </Section>
  );
}

function SeriesColorPickers({ meta, getSeriesColor, getConfiguredColor }) {
  const { seriesLabelsToIds } = meta;

  return meta.seriesLabels.map((series, i) => {
    const seriesId = seriesLabelsToIds[series];

    return (
      <Fragment key={series}>
        {i > 0 && <Divider />}
        <SeriesColorPicker
          title={series}
          series={series}
          seriesId={seriesId}
          getSeriesColor={getSeriesColor}
          getConfiguredColor={getConfiguredColor}
        />
      </Fragment>
    );
  });
}

function EmptySeriesColorPicker({
  chartType,
  getSeriesColor,
  getConfiguredColor,
}) {
  const series = '';

  const emptySeriesTitle =
    {
      BarChart: 'All bars',
      PieChart: 'All slices',
      LineChart: 'All lines',
      ScatterPlot: 'All points',
      ScatterBubblePlot: 'All bubbles',
    }[chartType] || 'All data';

  return (
    <SeriesColorPicker
      title={emptySeriesTitle}
      series={series}
      seriesId={series}
      getSeriesColor={getSeriesColor}
      getConfiguredColor={getConfiguredColor}
    />
  );
}

function SeriesColorPicker({
  title,
  series,
  seriesId,
  getSeriesColor,
  getConfiguredColor,
}) {
  const { get, set } = useReportConfiguration();

  // Use series name as color key in configuration. Fall back to seriesId to
  // support charts created before this change. See feature ADT-222.
  const seriesIdKey = getColorKey(seriesId);
  const seriesKey = getColorKey(series);
  const colorKey = get(seriesKey) ? seriesKey : seriesIdKey;

  return (
    <LabeledControl title={title || '(Blank)'} inline alignRight>
      <ColorPicker
        configurationKey={colorKey}
        defaultColor={getSeriesColor(series)}
        // Always store the color using the series name as the key.
        onChange={color => {
          set(seriesKey, color);
        }}
      />
      <Reset
        label={`Reset ${title} color`}
        // Reset color keys of both series and seriesId to support older charts.
        configurationKeys={[seriesIdKey, seriesKey]}
        disabled={!getConfiguredColor(series) && !getConfiguredColor(seriesId)}
      />
    </LabeledControl>
  );
}

export default SeriesConfig;
